import { useEffect, useMemo, useState } from "react";
import { TaskDisclosure } from "../components/TaskDisclosure";

function statusLabel(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function accessSubtitle(access) {
  if (access.status === "pending") {
    return "Pending account sign-in";
  }
  if (access.status === "disabled") {
    return "Access disabled";
  }
  return "Active account";
}

const ACCESS_APPLICATION_SCOPE_OPTIONS = [
  { key: "chapters", label: "Chapter directory", chapterOnly: true },
  { key: "membership", label: "Member records" },
  { key: "events", label: "Class schedule" },
  { key: "staff-portal", label: "Chapter leader workspace", chapterOnly: true },
  { key: "notifications", label: "Messages" },
  { key: "communications", label: "Notifications", legacy: true },
  { key: "payments", label: "Payments" },
  { key: "range-setup", label: "Owner setup" },
  { key: "daily-ops", label: "Range desk", physicalOnly: true }
];

const ACCESS_APPLICATION_SCOPE_LABELS = new Map(
  ACCESS_APPLICATION_SCOPE_OPTIONS.filter((option) => !option.legacy).map((option) => [option.key, option.label])
);

const APPLICATION_SCOPE_ALIASES = {
  communications: "notifications",
  messages: "notifications"
};

const ACCESS_FEATURE_CONTROL_OPTIONS = [
  {
    key: "createClasses",
    label: "Propose new classes",
    description: "Send new class requests to the owner for approval."
  },
  {
    key: "requestClassChanges",
    label: "Propose time changes",
    description: "Ask the owner to approve a different class time."
  },
  {
    key: "requestClassCancellation",
    label: "Propose cancellations",
    description: "Ask the owner to cancel a class with a reason."
  }
];

function toggleApplicationScope(currentScopes = [], scopeKey, checked) {
  const next = new Set(normalizeApplicationScopes(currentScopes));
  if (checked) {
    next.add(scopeKey);
  } else {
    next.delete(scopeKey);
  }
  return Array.from(next);
}

function toggleChapterAssignment(currentChapterIds = [], chapterId, checked) {
  const next = new Set(currentChapterIds || []);
  if (checked) {
    next.add(chapterId);
  } else {
    next.delete(chapterId);
  }
  return Array.from(next);
}

function nextChapterAssignmentsForAccess(access = {}, chapters = [], chapterId, checked) {
  const base = (access.scopeMode || "all") === "all"
    ? chapters.map((chapter) => chapter.chapterId).filter(Boolean)
    : access.chapterIds || [];
  return toggleChapterAssignment(base, chapterId, checked);
}

function accessHasApplicationScope(access = {}, scopeKey) {
  if ((access.scopeMode || "all") === "all") {
    return true;
  }
  return normalizeApplicationScopes(access.applicationScopes).includes(scopeKey);
}

function nextApplicationScopesForAccess(access = {}, options = [], scopeKey, checked) {
  const base = (access.scopeMode || "all") === "all"
    ? options.map((option) => option.key).filter(Boolean)
    : normalizeApplicationScopes(access.applicationScopes);
  return toggleApplicationScope(base, scopeKey, checked);
}

function normalizeApplicationScope(scope) {
  const key = String(scope || "").trim();
  return APPLICATION_SCOPE_ALIASES[key] || key;
}

function normalizeApplicationScopes(scopes = []) {
  return Array.from(new Set((scopes || []).map(normalizeApplicationScope).filter(Boolean)));
}

function normalizeFeaturePermissions(value = {}) {
  return {
    events: {
      createClasses: value.events?.createClasses === undefined ? true : Boolean(value.events.createClasses),
      requestClassChanges: value.events?.requestClassChanges === undefined ? true : Boolean(value.events.requestClassChanges),
      requestClassCancellation: value.events?.requestClassCancellation === undefined ? true : Boolean(value.events.requestClassCancellation)
    }
  };
}

function toggleEventFeaturePermission(currentPermissions = {}, key, checked) {
  const normalized = normalizeFeaturePermissions(currentPermissions);
  return {
    ...normalized,
    events: {
      ...normalized.events,
      [key]: checked
    }
  };
}

function parseChapterScope(text = "") {
  return String(text)
    .split(/\r?\n|,/)
    .map((value) => value.trim())
    .filter(Boolean);
}

function updateChapterScope(currentText = "", chapterId, checked) {
  const next = new Set(parseChapterScope(currentText));
  if (checked) {
    next.add(chapterId);
  } else {
    next.delete(chapterId);
  }
  return Array.from(next).join("\n");
}

function isDemoAccessIdentity(access = {}) {
  const email = String(access.email || "").toLowerCase();
  return /(?:@dev\.rangegrid\.local|rangegrid-.*audit|owner-audit|test@|example\.com)/i.test(email);
}

function updateScopedAccessForm(current, patch = {}) {
  const next = { ...current, ...patch };
  const selectedChapters = parseChapterScope(next.chapterIdsText);
  const selectedApps = Array.isArray(next.applicationScopes) ? normalizeApplicationScopes(next.applicationScopes) : [];
  if ((selectedChapters.length > 0 || selectedApps.length > 0) && next.scopeMode !== "limited") {
    next.scopeMode = "limited";
  }
  return next;
}

function chapterScopeSummary(access, chapters = []) {
  const chapterIds = access.chapterIds || [];
  if (!chapterIds.length) {
    return "";
  }
  const chapterNames = chapterIds
    .map((chapterId) => chapters.find((chapter) => chapter.chapterId === chapterId)?.name)
    .filter(Boolean);
  if (chapterNames.length > 0) {
    const missingCount = chapterIds.length - chapterNames.length;
    return missingCount > 0
      ? `${chapterNames.join(", ")} + ${missingCount} chapter${missingCount === 1 ? "" : "s"}`
      : chapterNames.join(", ");
  }
  return `${chapterIds.length} chapter${chapterIds.length === 1 ? "" : "s"} assigned`;
}

function scopeSummary(access, chapters = [], hasChapterWork = true) {
  if ((access.scopeMode || "all") === "all") {
    return hasChapterWork ? "All chapters and tools" : "All work areas";
  }
  const chapterSummary = chapterScopeSummary(access, chapters);
  if (chapterSummary) {
    return chapterSummary;
  }
  return "Limited work areas";
}

function applicationScopeLabels(access = {}, options = []) {
  if ((access.scopeMode || "all") === "all") {
    return ["All tools"];
  }
  return normalizeApplicationScopes(access.applicationScopes)
    .map((scope) => ACCESS_APPLICATION_SCOPE_LABELS.get(scope) || statusLabel(scope))
    .filter((label) => options.some((option) => option.label === label));
}

function classCapabilityLabels(access = {}) {
  const permissions = normalizeFeaturePermissions(access.featurePermissions).events;
  const labels = [];
  if (permissions.createClasses) {
    labels.push("New classes");
  }
  if (permissions.requestClassChanges) {
    labels.push("Time changes");
  }
  if (permissions.requestClassCancellation) {
    labels.push("Cancellations");
  }
  return labels;
}

export function AccessPage({
  accessForm,
  accessFilters,
  accessRecords,
  chapters = [],
  accessLoading,
  exportingAccess,
  grantingAccess,
  onExportAccess,
  onGrantAccess,
  onUpdateAccess,
  selectedRange,
  setAccessForm,
  setAccessFilters,
  updatingAccessUserId
}) {
  const chapterOrganizationWorkspace = selectedRange?.config?.templateKey === "chapter_organization"
    || selectedRange?.creationTemplate?.templateKey === "chapter_organization"
    || selectedRange?.templateKey === "chapter_organization"
    || selectedRange?.vocabulary?.chapterLabel;
  const activeChapters = useMemo(() => chapters.filter((chapter) => chapter.status !== "archived"), [chapters]);
  const hasChapterWork = chapterOrganizationWorkspace || activeChapters.length > 0;
  const applicationScopeOptions = useMemo(() => (
    ACCESS_APPLICATION_SCOPE_OPTIONS.filter((option) => (
      !option.legacy
      && (!chapterOrganizationWorkspace || !option.physicalOnly)
      && (!option.chapterOnly || hasChapterWork)
    ))
  ), [chapterOrganizationWorkspace, hasChapterWork]);
  const roleOptions = useMemo(() => ([
    { value: "staff", label: "Staff" },
    { value: "instructor", label: "Instructor" },
    ...(hasChapterWork ? [{ value: "chapter_leader", label: "Chapter leader" }] : []),
    { value: "admin", label: "Admin" }
  ]), [hasChapterWork]);
  const visibleAccessRecords = useMemo(() => (
    (accessRecords || [])
      .filter((access) => !isDemoAccessIdentity(access))
      .filter((access, index, records) => {
        if (access.status !== "disabled") {
          return true;
        }
        const chapterKey = (access.chapterIds || []).slice().sort().join("|");
        const duplicateActiveAccess = records.some((candidate, candidateIndex) => {
          if (candidateIndex === index || candidate.status === "disabled") {
            return false;
          }
          const candidateChapterKey = (candidate.chapterIds || []).slice().sort().join("|");
          return String(candidate.email || "").toLowerCase() === String(access.email || "").toLowerCase()
            && candidate.role === access.role
            && (candidate.scopeMode || "all") === (access.scopeMode || "all")
            && candidateChapterKey === chapterKey;
        });
        return !duplicateActiveAccess;
      })
  ), [accessRecords]);
  const selectedChapterIds = useMemo(() => new Set(parseChapterScope(accessForm.chapterIdsText)), [accessForm.chapterIdsText]);
  const [activeAccessSection, setActiveAccessSection] = useState("people");
  const [expandedAccessUserId, setExpandedAccessUserId] = useState("");
  useEffect(() => {
    function syncAccessSectionFromHash() {
      const hash = window.location.hash.replace("#access-", "");
      if (hash === "people") {
        setActiveAccessSection(hash);
      }
    }

    syncAccessSectionFromHash();
    window.addEventListener("hashchange", syncAccessSectionFromHash);
    return () => window.removeEventListener("hashchange", syncAccessSectionFromHash);
  }, []);
  const accessSections = [
    { id: "people", label: "Team access", meta: `${visibleAccessRecords.length} people` }
  ];

  function switchAccessSection(sectionId) {
    setActiveAccessSection(sectionId);
    if (typeof window !== "undefined") {
      window.history.replaceState(null, "", `#access-${sectionId}`);
    }
  }

  return (
    <section className="panel page-panel" id="access">
      {!selectedRange && <div className="empty-state">Select a range before managing access.</div>}
      {selectedRange && (
        <>
        {accessSections.length > 1 && (
          <nav className="console-section-nav" aria-label="Access sections">
            {accessSections.map((section) => (
              <button
                type="button"
                className={activeAccessSection === section.id ? "is-active" : ""}
                key={section.id}
                onClick={() => switchAccessSection(section.id)}
              >
                <span>{section.label}</span>
                <small>{section.meta}</small>
              </button>
            ))}
          </nav>
        )}

        {activeAccessSection === "people" && (
        <div className="access-management-grid access-management-grid-primary">
          <TaskDisclosure
            actionLabel="Invite"
            title="Invite someone"
            description={hasChapterWork
              ? "Give a teammate access to the right chapters and tools."
              : "Give a teammate access to the right work areas."}
          >
          <form className="member-form" onSubmit={onGrantAccess}>
            <div className="panel-header">
              <h3>Invite teammate</h3>
              <p>Choose their role and the work areas they can open.</p>
            </div>

            <label>
              Email
              <input
                type="email"
                value={accessForm.email}
                onChange={(event) => setAccessForm((current) => ({ ...current, email: event.target.value }))}
                placeholder="staff@example.com"
                required
              />
            </label>

            <label>
              Role
                <select
                  value={accessForm.role}
                onChange={(event) => setAccessForm((current) => {
                  const role = event.target.value;
                  return updateScopedAccessForm(current, {
                    role,
                    scopeMode: ["chapter_leader", "instructor"].includes(role) ? "limited" : current.scopeMode
                  });
                })}
              >
                {roleOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>

            <div className="access-scope-editor">
              <div className="panel-header compact-panel-header">
                <h4>Where they work</h4>
                <p>Limit teammates to the records and tools they actually need.</p>
              </div>
              <label>
                Coverage
                <select
                  value={accessForm.scopeMode}
                  onChange={(event) => setAccessForm((current) => {
                    const scopeMode = event.target.value;
                    return scopeMode === "all"
                      ? { ...current, scopeMode, chapterIdsText: "", applicationScopes: [] }
                      : { ...current, scopeMode };
                  })}
                >
                  <option value="all">Everything</option>
                  <option value="limited">Choose work areas</option>
                </select>
              </label>
              {accessForm.scopeMode === "limited" && hasChapterWork && activeChapters.length > 0 ? (
                <div className="access-scope-checkboxes access-chapter-scope" aria-label="Assigned chapters">
                  {activeChapters.map((chapter) => (
                    <label key={chapter.chapterId}>
                      <input
                        type="checkbox"
                        checked={selectedChapterIds.has(chapter.chapterId)}
                        onChange={(event) => setAccessForm((current) => ({
                          ...updateScopedAccessForm(current, {
                            chapterIdsText: updateChapterScope(current.chapterIdsText, chapter.chapterId, event.target.checked)
                          })
                        }))}
                      />
                      <span>{chapter.name}</span>
                    </label>
                  ))}
                </div>
              ) : null}
              {accessForm.scopeMode === "limited" ? (
                <div className="access-scope-checkboxes" aria-label="Visible work areas">
                  {applicationScopeOptions.map((option) => (
                    <label key={option.key}>
                      <input
                        type="checkbox"
                        checked={normalizeApplicationScopes(accessForm.applicationScopes).includes(option.key)}
                        onChange={(event) => setAccessForm((current) => ({
                          ...updateScopedAccessForm(current, {
                            applicationScopes: toggleApplicationScope(current.applicationScopes, option.key, event.target.checked)
                          })
                        }))}
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
              ) : null}
              {accessForm.role === "chapter_leader" && (
                <div className="access-feature-panel" aria-label="Chapter leader class controls">
                  <div className="compact-panel-header">
                    <h4>Class requests</h4>
                    <p>Choose which class changes this chapter leader can send for approval.</p>
                  </div>
                  {ACCESS_FEATURE_CONTROL_OPTIONS.map((option) => (
                    <label key={option.key}>
                      <input
                        type="checkbox"
                        checked={normalizeFeaturePermissions(accessForm.featurePermissions).events[option.key]}
                        onChange={(event) => setAccessForm((current) => ({
                          ...current,
                          featurePermissions: toggleEventFeaturePermission(current.featurePermissions, option.key, event.target.checked)
                        }))}
                      />
                      <span>
                        <strong>{option.label}</strong>
                        <small>{option.description}</small>
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <button type="submit" disabled={grantingAccess || !accessForm.email.trim()}>
              {grantingAccess ? "Inviting..." : "Send invite"}
            </button>
          </form>
          </TaskDisclosure>

          <div className="member-list">
            <div className="panel-header access-list-header">
              <div>
                <h3>Team access</h3>
              </div>
              <button type="button" className="secondary-button" disabled={exportingAccess || visibleAccessRecords.length === 0} onClick={onExportAccess}>
                {exportingAccess ? "Exporting..." : "Export"}
              </button>
            </div>
            <div className="form-grid compact-form-grid">
              <label>
                Role
                <select value={accessFilters.role} onChange={(event) => setAccessFilters((current) => ({ ...current, role: event.target.value }))}>
                  <option value="">All roles</option>
                  <option value="owner">Owner</option>
                  <option value="admin">Admin</option>
                  {hasChapterWork && <option value="chapter_leader">Chapter leader</option>}
                  <option value="instructor">Instructor</option>
                  <option value="staff">Staff</option>
                </select>
              </label>
              <label>
                Status
                <select value={accessFilters.status} onChange={(event) => setAccessFilters((current) => ({ ...current, status: event.target.value }))}>
                  <option value="">All statuses</option>
                  <option value="active">Active</option>
                  <option value="pending">Pending</option>
                  <option value="disabled">Disabled</option>
                </select>
              </label>
              <label>
                Search
                <input
                  value={accessFilters.q}
                  onChange={(event) => setAccessFilters((current) => ({ ...current, q: event.target.value }))}
                  placeholder="Name or email"
                />
              </label>
              <button
                type="button"
                className="secondary-button"
                disabled={!accessFilters.role && !accessFilters.status && !accessFilters.q}
                onClick={() => setAccessFilters({ role: "", status: "", q: "" })}
              >
                Clear filters
              </button>
            </div>
            {accessLoading && <div className="empty-state">Loading access...</div>}
            {!accessLoading && visibleAccessRecords.length === 0 && <div className="empty-state">No people have access yet.</div>}
            {!accessLoading && visibleAccessRecords.map((access) => {
              const isUpdating = updatingAccessUserId === access.accessUserId;
              const isOwner = access.role === "owner";
              const accessExpanded = expandedAccessUserId === access.accessUserId;
              const classLabels = classCapabilityLabels(access);
              const workspaceLabels = applicationScopeLabels(access, applicationScopeOptions);
              return (
                <div className={`access-row${accessExpanded ? " is-expanded" : ""}${isOwner ? " is-owner" : ""}`} key={access.accessUserId}>
                  <div className="access-row-person">
                    <strong>{access.email || "Team member"}</strong>
                    <span>{accessSubtitle(access)}</span>
                  </div>
                  <div className="access-row-summary">
                    <span>{scopeSummary(access, activeChapters, hasChapterWork)}</span>
                    {access.role === "chapter_leader" && (
                      <div className="access-capability-list" aria-label="Tools this person can open">
                        {workspaceLabels.length ? workspaceLabels.slice(0, 4).map((label) => (
                          <span key={label}>{label}</span>
                        )) : <span className="is-muted">No tools selected</span>}
                        {workspaceLabels.length > 4 && <span>+{workspaceLabels.length - 4}</span>}
                      </div>
                    )}
                    {access.role === "chapter_leader" && (
                      <div className="access-capability-list access-capability-list-secondary" aria-label="Class request permissions">
                        {classLabels.length ? classLabels.map((label) => (
                          <span key={label}>{label}</span>
                        )) : <span className="is-muted">Class requests disabled</span>}
                      </div>
                    )}
                  </div>
                  <div className="resource-state access-row-state">
                    <span className={`member-status status-${access.status}`}>{statusLabel(access.status)}</span>
                    {isOwner && <span className="app-state">{statusLabel(access.role)}</span>}
                  </div>
                  <div className="row-actions access-row-actions">
                    {isOwner ? (
                      <span className="access-owner-note">Protected owner access</span>
                    ) : (
                      <>
                        <select
                          value={access.role}
                          disabled={isUpdating}
                          onChange={(event) => onUpdateAccess(access.accessUserId, { role: event.target.value })}
                          aria-label={`Role for ${access.email || "team member"}`}
                        >
                          {roleOptions.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                        <div className="access-row-action-buttons">
                          {access.role === "chapter_leader" && (
                            <button
                              type="button"
                              className="secondary-button"
                              onClick={() => setExpandedAccessUserId((current) => current === access.accessUserId ? "" : access.accessUserId)}
                            >
                              {accessExpanded ? "Done" : "Adjust"}
                            </button>
                          )}
                          {access.status === "disabled" ? (
                            <button
                              type="button"
                              className="secondary-button"
                              disabled={isUpdating}
                              onClick={() => onUpdateAccess(access.accessUserId, { status: "active" })}
                            >
                              Restore access
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="secondary-button"
                              disabled={isUpdating}
                              onClick={() => onUpdateAccess(access.accessUserId, { status: "disabled" })}
                            >
                              Disable
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  {!isOwner && access.role === "chapter_leader" && accessExpanded && (
                    <div className="access-row-controls">
                      <div className="access-row-controls-heading">
                        <strong>Chapter leader access</strong>
                      </div>
                      <div className="access-row-control-groups">
                        <div className="access-row-control-group">
                          <strong>Assigned chapters</strong>
                          <div className="access-row-feature-controls" aria-label={`Assigned chapters for ${access.email || "chapter leader"}`}>
                            {activeChapters.map((chapter) => {
                              const assignedChapterIds = (access.scopeMode || "all") === "all"
                                ? activeChapters.map((item) => item.chapterId)
                                : access.chapterIds || [];
                              return (
                                <label key={chapter.chapterId}>
                                  <input
                                    type="checkbox"
                                    checked={assignedChapterIds.includes(chapter.chapterId)}
                                    disabled={isUpdating}
                                    onChange={(event) => onUpdateAccess(access.accessUserId, {
                                      scopeMode: "limited",
                                      chapterIds: nextChapterAssignmentsForAccess(access, activeChapters, chapter.chapterId, event.target.checked)
                                    })}
                                  />
                                  <span>{chapter.name}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                        <div className="access-row-control-group">
                          <strong>Apps this person can open</strong>
                          <div className="access-row-feature-controls" aria-label={`Apps ${access.email || "chapter leader"} can open`}>
                            {applicationScopeOptions
                              .filter((option) => ["membership", "events", "notifications", "staff-portal"].includes(option.key))
                              .map((option) => (
                              <label key={option.key}>
                                <input
                                  type="checkbox"
                                  checked={accessHasApplicationScope(access, option.key)}
                                  disabled={isUpdating}
                                  onChange={(event) => onUpdateAccess(access.accessUserId, {
                                    scopeMode: "limited",
                                    applicationScopes: nextApplicationScopesForAccess(
                                      access,
                                      applicationScopeOptions.filter((scopeOption) => ["membership", "events", "notifications", "staff-portal"].includes(scopeOption.key)),
                                      option.key,
                                      event.target.checked
                                    )
                                  })}
                                />
                                <span>{option.label}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                        <div className="access-row-control-group">
                          <strong>Class requests</strong>
                          <div className="access-row-feature-controls" aria-label={`Class controls for ${access.email || "chapter leader"}`}>
                            {ACCESS_FEATURE_CONTROL_OPTIONS.map((option) => (
                              <label key={option.key}>
                                <input
                                  type="checkbox"
                                  checked={normalizeFeaturePermissions(access.featurePermissions).events[option.key]}
                                  disabled={isUpdating}
                                  onChange={(event) => onUpdateAccess(access.accessUserId, {
                                    featurePermissions: toggleEventFeaturePermission(access.featurePermissions, option.key, event.target.checked)
                                  })}
                                />
                                <span>{option.label}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        )}

        </>
      )}
    </section>
  );
}
